import React, { useMemo } from 'react'
import { rem } from 'polished'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import MerchandiseCarouselSection from '../MerchandiseCarouselSection'
import MerchandiseBrandCard from '../MerchandiseCards/MerchandiseBrandCard'
import useMerchandiseBrandsList from 'hooks/Merchandise/useMerchandiseBrandsList'
import TrackedCardCarousel from 'components/Common/TrackedCardCarousel/TrackedCardCarousel'
import styled from 'styled-components'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import LayoutContainer from 'components/Common/LayoutContainer'
import { useIsMobileDevice } from 'hooks/useIsMobileDevice'
import { splitByWeight } from 'lib/array/arrayUtils'
import { useIntl } from 'react-intl'

const MOBILE_GROUP_SIZE = 4

const MobileGridContainer = styled.div`
  display: grid;
  gap: ${rem(12)};
  grid-template-columns: repeat(2, 1fr);
  grid-template-areas:
    "a c"
    "b d";

  & > :nth-child(1) {
    grid-area: a;
  }
  & > :nth-child(2) {
    grid-area: b;
  }
  & > :nth-child(3) {
    grid-area: c;
  }
  & > :nth-child(4) {
    grid-area: d;
  }
`

interface Props {
  title: React.ReactNode
  subtitle?: React.ReactNode
  viewAllTo?: string
  /**
   * Supply filters to have the carousel manage
   * the list of brands that will be shown
   */
  filters?: App.MerchandiseBrandFilters
  /**
   * Provide your own list of brand ids
   */
  overrideList?: Array<string>
  /**
   * Provide your own state for whether the list is fetching or not
   */
  overrideFetching?: boolean
}

function MerchandiseBrandsCarousel(props: Props) {
  const intl = useIntl()
  const { title, filters, subtitle, viewAllTo, overrideList, overrideFetching } = props

  const isMobileScreen = useIsMobileDevice()
  const itemsToShow = isMobileScreen ? 2 : 6

  const [brandList, fetchingList] = useMerchandiseBrandsList(filters, { disabled: !!overrideList })

  const finalList = overrideList ?? brandList
  const finalFetching = overrideFetching ?? fetchingList

  const groupList = useMemo(() => splitByWeight(finalList, { limit: MOBILE_GROUP_SIZE }), [finalList])

  return <MerchandiseCarouselSection
    title={title}
    subtitle={subtitle}
    viewAllTo={viewAllTo}
    viewAllText={intl.formatMessage({
      id: 'pCdZLHnwj7',
      defaultMessage: 'View all',
      description: 'View all button text for brands carousel',
    })}
    trackingName="brands"
  >
    <Group direction="vertical" gap={32}>
      <LayoutContainer>
        <Group direction="vertical" gap={8}>
          <CSSBreakpoint min="tablet">
            <TrackedCardCarousel pageSize={itemsToShow} gap={12}>
              {!finalFetching && finalList.map((brandId, index) => <MerchandiseBrandCard
                key={brandId}
                id={brandId}
                index={index}
              />)}

              {finalFetching && <MobileGridContainer>
                {Array.from({ length: itemsToShow }).map((_, index) => (
                  <LoadingBox key={index} width={170} height={104} />
                ))}
              </MobileGridContainer>}
            </TrackedCardCarousel>
          </CSSBreakpoint>

          <CSSBreakpoint only="mobile">
            <TrackedCardCarousel pageSize={itemsToShow} gap={12}>
              {!finalFetching && groupList.map((group, groupIndex) => <MobileGridContainer key={groupIndex}>
                {group.map((brandId, index) => <MerchandiseBrandCard
                  key={brandId}
                  id={brandId}
                  index={groupIndex * MOBILE_GROUP_SIZE + index}
                />)}
              </MobileGridContainer>)}

              {finalFetching && <>
                {Array.from({ length: itemsToShow }).map((_, index) => (
                  <LoadingBox key={index} width={170} height={104} />
                ))}
              </>}
            </TrackedCardCarousel>
          </CSSBreakpoint>
        </Group>
      </LayoutContainer>
    </Group>
  </MerchandiseCarouselSection>
}

export default MerchandiseBrandsCarousel
